import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { CancelReimbursementModal } from '@/common/components/CancelReimbursementModal/CancelReimbursementModal';
import { CardActionDownload } from '@/common/components/CardActionDownload/CardActionDownload';
import { LabeledValueList } from '@/common/components/LabeledValueList/LabeledValueList';
import { getReimbursementBadge } from '@/common/components/OperationsList/OperationsList.utils';
import { Stack } from '@/common/components/Stack/Stack';
import {
  TEducationLevel,
  educationMapping,
} from '@/common/constants/education';
import {
  IOperationReimbursementExpense,
  TMenhirOperationStatus,
} from '@/common/integrations/menhir/employee/operations/operations.types';
import { benefitRouteTypeMapping } from '@/common/utils/benefits';
import { formatDate } from '@/common/utils/date';
import { formatTaxId } from '@/common/utils/tax';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';

export type TReimbursementDetailDrawerProps = {
  operation: IOperationReimbursementExpense;
};

export const ReimbursementDetailDrawer = ({
  operation,
}: TReimbursementDetailDrawerProps) => {
  const { t } = useTranslation();

  const [displayCancelModal, toggleCancelModal] = useState(false);
  const { reimbursementOrder } = operation;
  const educationKey =
    educationMapping[reimbursementOrder.educationLevel as TEducationLevel];

  return (
    <Stack gap="lg">
      <View className="self-start">
        {getReimbursementBadge(
          operation.reimbursementOrder.status as TMenhirOperationStatus,
          t,
          'lg'
        )}
      </View>
      {reimbursementOrder.status === 'rejected' && (
        <Stack>
          <BodyText>
            {t('dashboard.reimbursement_details.rejected_reason')}
          </BodyText>
          <BodyText bold>{reimbursementOrder.statusDetails?.reason}</BodyText>
        </Stack>
      )}

      {displayCancelModal && operation.categorySlug && (
        <CancelReimbursementModal
          orderId={reimbursementOrder.id}
          category={operation.categorySlug}
          onCancel={() => {
            toggleCancelModal(false);
          }}
          onClose={() => toggleCancelModal(false)}
        />
      )}

      <Stack gap="xl">
        <Stack gap="xl">
          <LabeledValueList.Root
            title={t(
              'dashboard.welfare.reimbursements.steps.confirm.invoice_details.title'
            )}
          >
            <LabeledValueList.Item
              label={t(
                'dashboard.welfare.reimbursements.steps.confirm.invoice_details.number'
              )}
              value={reimbursementOrder.invoiceNumber}
            />
            <LabeledValueList.Item
              label={t(
                'dashboard.welfare.reimbursements.steps.confirm.invoice_details.amount'
              )}
              value={MoneyUtils.moneyToString(operation.amount)}
            />
            <LabeledValueList.Item
              label={t('dashboard.reimbursement_details.request_date')}
              value={formatDate(reimbursementOrder.insertedAt)}
            />
            <LabeledValueList.Item
              label={t(
                'dashboard.welfare.reimbursements.steps.confirm.invoice_details.date'
              )}
              value={formatDate(reimbursementOrder.purchasedAt)}
            />
            {reimbursementOrder.educationLevel ? (
              <LabeledValueList.Item
                label={t(
                  'dashboard.welfare.reimbursements.steps.confirm.invoice_details.expense_education_level'
                )}
                value={t(educationKey)}
              />
            ) : undefined}
            <LabeledValueList.Item
              label={t(
                'dashboard.welfare.reimbursements.steps.confirm.invoice_details.fiscal_year'
              )}
              value={reimbursementOrder.fiscalYear}
            />
          </LabeledValueList.Root>
          {reimbursementOrder.beneficiary && (
            <LabeledValueList.Root
              title={t(
                'dashboard.welfare.reimbursements.steps.confirm.beneficiary_details.title'
              )}
            >
              <LabeledValueList.Item
                label={t(
                  'dashboard.welfare.reimbursements.steps.confirm.beneficiary_details.full_name'
                )}
                value={reimbursementOrder.beneficiary.fullName}
              />
              <LabeledValueList.Item
                label={t(
                  'dashboard.welfare.reimbursements.steps.confirm.beneficiary_details.tax_number'
                )}
                value={formatTaxId(reimbursementOrder.beneficiary.taxId, '-')}
              />
            </LabeledValueList.Root>
          )}
          {(reimbursementOrder.status === 'pending_approval' ||
            reimbursementOrder.status === 'approved') && (
            <LabeledValueList.Root
              title={t(
                'dashboard.welfare.reimbursements.steps.confirm.expense_transferred.title'
              )}
            >
              <LabeledValueList.Item
                value={reimbursementOrder.iban}
                label={t(
                  'dashboard.welfare.reimbursements.steps.confirm.expense_transferred.iban'
                )}
              ></LabeledValueList.Item>
            </LabeledValueList.Root>
          )}
        </Stack>

        <Stack gap="sm">
          {reimbursementOrder.files
            .filter((file) => file.fileType === 'invoice')
            .map((file) => (
              <CardActionDownload
                key={file.id}
                title={file.name}
                size="sm"
                fileUrl={file.url ?? ''}
              />
            ))}
        </Stack>

        {!!reimbursementOrder.files.some(
          (file) => file.fileType === 'support'
        ) && (
          <Stack gap="lg">
            <BodyText bold size="md">
              {t('dashboard.reimbursement_details.additional_files.title')}
            </BodyText>
            <Stack gap="sm">
              {reimbursementOrder.files
                .filter((file) => file.fileType === 'support')
                .map((file) => (
                  <CardActionDownload
                    key={file.id}
                    title={file.name}
                    size="sm"
                    fileUrl={file.url ?? ''}
                  />
                ))}
            </Stack>
          </Stack>
        )}
        {reimbursementOrder.status === 'pending_approval' && (
          <Stack align="start">
            <BodyText>
              {t('dashboard.reimbursement_details.cancel_disclaimer')}
            </BodyText>
            <Button
              variant="tertiary"
              size="sm"
              onPress={() => toggleCancelModal(true)}
            >
              {t('dashboard.reimbursement_details.cancel_request')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export function ReimbursementDetailDrawerTitle({
  operation,
}: TReimbursementDetailDrawerProps) {
  const { t } = useTranslation();

  return t('operations.detail.reimbursement_label', {
    category: t(
      `dashboard.benefits.links.${benefitRouteTypeMapping[operation.categorySlug ?? '']}`
    ),
  });
}
